/**
 * Deleting mail for real: the account withdraws the wraps addressed to it.
 *
 * A gift wrap is signed by a throwaway key, so NIP-09 can never delete one.
 * ps-relay honours kind 1316 instead: signed by the account, one e tag per wrap,
 * it deletes each named wrap addressed to the signer and refuses it if it is
 * sent again.
 *
 * The requests stay on the relay as regular events, and they are the record:
 * another device reads the ones newer than it last saw and drops that
 * plaintext. Nothing is ever replaced, so there is no list to outgrow and no
 * version to race.
 */
import type { Event as NostrEvent } from "nostr-tools";
import type { RelayConnection } from "./relay";
import type { MailSigner } from "./signer";

export const KIND_WITHDRAWN = 1316;
/** ps-relay's cap per request. Larger deletes are several requests. */
export const WITHDRAW_BATCH = 500;

/**
 * Withdraw these wraps. One signature per batch of 500.
 *
 * Returns the newest request's timestamp, so the caller does not read its own
 * requests back as news.
 */
export async function publishWithdrawn(
  relay: RelayConnection,
  signer: MailSigner,
  ids: readonly string[],
): Promise<number> {
  const unique = [...new Set(ids.map((id) => id.trim().toLowerCase()).filter(Boolean))];
  let newest = 0;
  for (let i = 0; i < unique.length; i += WITHDRAW_BATCH) {
    const event = await signer.signEvent({
      kind: KIND_WITHDRAWN,
      created_at: Math.floor(Date.now() / 1000),
      tags: unique.slice(i, i + WITHDRAW_BATCH).map((id) => ["e", id]),
      content: "",
    });
    await relay.publish(event);
    newest = Math.max(newest, event.created_at);
  }
  return newest;
}

/** Every wrap this account withdrew since `since`, and the newest request's time. */
export async function fetchWithdrawn(
  relay: RelayConnection,
  pubkey: string,
  since = 0,
): Promise<{ ids: string[]; newest: number }> {
  const events: NostrEvent[] = await relay.list({
    kinds: [KIND_WITHDRAWN],
    authors: [pubkey],
    ...(since > 0 ? { since } : {}),
  });
  const ids = new Set<string>();
  let newest = since;
  for (const event of events) {
    newest = Math.max(newest, event.created_at);
    for (const tag of event.tags) if (tag[0] === "e" && tag[1]) ids.add(tag[1].toLowerCase());
  }
  return { ids: [...ids], newest };
}
